import { BoxProps, CircularProgress, Typography, useTheme } from '@mui/material'
import { ButtonEx, FlexBoxProps, FlexGrowRow, FlexRow, QuickTipButton } from '@xylabs/sdk-react'
import { ReactElement } from 'react'

export interface PropertyAction {
  disabled?: boolean
  name: string
  onClick?: () => void
}

export interface PropertyProps extends FlexBoxProps {
  actions?: PropertyAction[]
  children?: ReactElement[] | ReactElement | null | string | number | undefined
  maxTitleWidth?: number
  required?: boolean
  tip?: string
  title?: string
  value?: string | number | boolean | null
}

interface ValueProps extends FlexBoxProps {
  value?: string | number | boolean | null
}

const Value: React.FC<ValueProps> = ({ value, ...props }) => {
  return <FlexGrowRow {...props}>{value !== undefined ? <Typography variant="caption">{value}</Typography> : null}</FlexGrowRow>
}

interface TitleProps extends BoxProps {
  tip?: string
  title?: string
}

const Title: React.FC<TitleProps> = (props) => {
  const { tip, title, ...boxProps } = props
  const theme = useTheme()
  return (
    <FlexRow borderRight={`solid 1px ${theme.palette.divider}`} justifyContent="flex-start" paddingX={2} paddingY={0.5} {...boxProps}>
      {tip ? <QuickTipButton title={title ?? ''}>{tip}</QuickTipButton> : null}
      <Typography noWrap variant="caption">
        {title}
      </Typography>
    </FlexRow>
  )
}

const Property: React.FC<PropertyProps> = ({ title, value, children, maxTitleWidth = 180, tip, actions, required, ...props }) => {
  const theme = useTheme()

  const Actions: React.FC<FlexBoxProps> = (props) => {
    if (actions) {
      return (
        <FlexRow {...props}>
          {actions.length > 0 ? (
            <FlexRow>
              {actions.map((action, index) => {
                return (
                  <ButtonEx key={index} marginRight={1} disabled={action.disabled} onClick={action.onClick} variant="contained">
                    {action.name}
                  </ButtonEx>
                )
              })}
            </FlexRow>
          ) : null}
        </FlexRow>
      )
    }
    return null
  }

  return (
    <FlexRow
      paper
      margin={1}
      paddingY={1}
      borderColor={required && value === undefined ? theme.palette.error.main : theme.palette.divider}
      border="1px gray solid"
      {...props}
    >
      <FlexGrowRow minHeight={56}>
        {title ? <Title maxWidth={maxTitleWidth} tip={tip} title={title} /> : null}
        {value === undefined ? (
          <FlexRow paddingX={2}>
            <CircularProgress size={16} />
          </FlexRow>
        ) : (
          <FlexGrowRow flexWrap="wrap" justifyContent="space-between" paddingX={2}>
            <FlexRow marginX={1}>{children ? children : <Value value={value} marginX={1} />}</FlexRow>
            <Actions marginLeft={1} />
          </FlexGrowRow>
        )}
      </FlexGrowRow>
    </FlexRow>
  )
}

export default Property
